import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Row, Col, InputNumber, Select, DatePicker, Card, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined, PrinterOutlined, FileAddOutlined } from '@ant-design/icons';
import { usePurchase } from '../../store/purchaseContext';
import { useProduct } from '../../store/productContext';
import { useSupplier } from '../../store/supplierContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { PurchaseInvoice } from '../../types/purchase';
import { useReactToPrint } from 'react-to-print';
import PrintableInvoice from '../Print/PrintableInvoice';
import { useTranslation } from 'react-i18next'; // 1. Import translation hook
import ItemStockHint from '../Product/ItemStockHint';

const UNITS = [
    { value: "piece", label: "Piece" },
    { value: "set", label: "Set" },
    { value: "carton", label: "Carton" },
    { value: "meter", label: "Meter" },
    { value: "roll", label: "Roll" },
    { value: "box", label: "Box" },
    { value: "other", label: "Other" },
];

interface PurchaseFormProps {
    editPurchase?: PurchaseInvoice;
    onFinishEdit?: () => void;
}

const PurchaseForm: React.FC<PurchaseFormProps> = ({ editPurchase, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addPurchase, updatePurchase } = usePurchase();
    const { products } = useProduct();
    const { suppliers, fetchSuppliers } = useSupplier();
    const { currencies } = useCurrency();
    const [currenciesDisabled, setCurrenciesDisabled] = useState(false);

    const [newInvoice, setNewInvoice] = useState<PurchaseInvoice | null>(null);
    const printRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation(); // 2. Initialize the translation hook

    const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
    const currencyList = currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    }));

    useEffect(() => {
        if (!editPurchase) {
            form.setFieldsValue({
                date: dayjs(),
                items: [{}],
                paid: 0,
                remain: 0,
                total: 0,
                currency: DEFAULT_CURRENCY,
            });
        } else {
            // Normalize supplier: extract ID if backend returns a populated object
            const supplierValue = editPurchase.supplier && typeof editPurchase.supplier === 'object'
                ? (editPurchase.supplier as any).id || (editPurchase.supplier as any)._id
                : editPurchase.supplier;

            // Normalize items: extract product ID if backend returns populated objects
            const itemsValue = editPurchase.items?.map(item => {
                let productValue: any = item.product;
                if (item.product && typeof item.product === 'object') {
                    productValue = (item.product as any).id || (item.product as any)._id;
                }
                return { ...item, product: productValue };
            }) || [];

            form.setFieldsValue({
                ...editPurchase,
                supplier: supplierValue,
                items: itemsValue,
                date: editPurchase.date ? dayjs(editPurchase.date) : dayjs(),
            });
        }
        fetchSuppliers();
        setCurrenciesDisabled(isItemsFilled());
        // eslint-disable-next-line
    }, [editPurchase, DEFAULT_CURRENCY, form]);

    const isItemsFilled = () => {
        const items = form.getFieldValue('items') || [];
        return items.some((item: any) =>
            item && (item.product || item.qty || item.buyPrice)
        );
    };

    const supplierOptions = suppliers.map(s => ({
        value: s.id,
        label: s.name,
    }));

    const productOptions = products.map(p => ({
        value: p.id,
        label: p.name,
    }));

    const getSupplierName = (supplierId: string) => {
        return suppliers.find(s => s.id === supplierId)?.name || '-';
    };

    const getProductName = (id: string) => {
        return products.find(p => p.id === id)?.name || '-';
    };

    const handleProductSelect = (productId: string, idx: number) => {
        const invoiceCurrency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
        const prod = products.find(p => p.id === productId);
        let price = 0;
        if (prod && Array.isArray(prod.currentStock)) {
            if (invoiceCurrency === "USD") {
                price = prod.buyPriceInDollar;
            } else if (invoiceCurrency === "IQD") {
                price = prod.buyPriceInDinar;
            }
        }
        const items = form.getFieldValue('items') || [];
        items[idx] = { ...items[idx], unit: "piece", qty: 1, buyPrice: price };
        form.setFieldsValue({ items });
        handleItemChange({}, { items });
    };

    const handleItemChange = (changed: any, all: any) => {
        const items = Array.isArray(all.items) ? all.items : [];
        const total = items.reduce((sum: number, item: any) => {
            if (item && item.qty && item.buyPrice) return sum + (Number(item.qty) * Number(item.buyPrice));
            return sum;
        }, 0);
        if (form.getFieldValue('total') !== total) {
            form.setFieldsValue({ total });
        }
        const paid = form.getFieldValue('paid') || 0;
        form.setFieldsValue({ remain: Math.max(total - paid, 0) });
        setCurrenciesDisabled(isItemsFilled());
    };

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: t('purchase_print') as string,
    });

    const handleNewInvoice = () => {
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(),
            items: [{}],
            paid: 0,
            remain: 0,
            total: 0,
            currency: DEFAULT_CURRENCY,
        });
        setCurrenciesDisabled(false);
        setNewInvoice(null);
    };

    const focusOnNewProductInput = (idx: number) => {
        setTimeout(() => {
            const el = document.getElementById(`item-${idx}`);
            if (el) (el as HTMLElement).focus();
        }, 80);
    };

    // Remove incomplete items BEFORE sending to the endpoint:
    const onFinish = async (values: any) => {
        const cleanedItems = (values.items || []).filter(
            (item: any) => item.product && item.qty && item.buyPrice
        );

        if (!cleanedItems.length) {
            message.error(t("purchase_at_least_one_item") as string);
            return;
        }

        const items = cleanedItems.map((item: any) => ({
            product: typeof item.product === "object" ? item.product.value || item.product.id : item.product,
            unit: item.unit,
            qty: Number(item.qty),
            unitLength: item.unitLength ? Number(item.unitLength) : undefined,
            buyPrice: Number(item.buyPrice),
            attributes: item.attributes || undefined,
        }));

        const payload = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
            items,
            supplier: values.supplier,
        };
        if (editPurchase) {
            updatePurchase({ ...editPurchase, ...payload });
            onFinishEdit?.();
            form.resetFields();
        } else {
            const created = await addPurchase(payload);
            setNewInvoice(created);
            setTimeout(() => {
                handlePrint();
            }, 300);
            form.setFieldsValue({ items: [{}], currency: DEFAULT_CURRENCY });
        }
    };

    return (
        <>
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{
                maxWidth: 1400,
                minWidth: 480,
                margin: '0 auto',
                padding: 16,
                background: "#fff",
                borderRadius: 18,
                boxShadow: '0 2px 16px rgba(39,21,107,0.06)'
            }}
            onValuesChange={handleItemChange}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col xs={24} md={12}>
                        <Form.Item name="supplier" label={t("purchase_supplier")} rules={[{ required: true }]}>
                        <Select
                            options={supplierOptions}
                                placeholder={t("purchase_select_supplier") as string}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                        <Form.Item name="currency" label={t("purchase_currency")} rules={[{ required: true }]}>
                            <Select disabled={currenciesDisabled} options={currencyList} placeholder={t("purchase_currency") as string} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                        <Form.Item name="date" label={t("purchase_date")} rules={[{ required: true }]}>
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>
                <Card title={t("purchase_items")} style={{ marginBottom: 16, borderRadius: 12 }}>
                <Form.List
                    name="items"
                    rules={[
                        {
                            validator: async (_, items) => {
                                if (!items || items.length < 1 || items.every((item: any) => !item.product)) {
                                    return Promise.reject(new Error(t("purchase_at_least_one_item") as string));
                                }
                            }
                        }
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            <Row gutter={8} style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <Col span={6}>{t("purchase_product")}</Col>
                                    <Col span={3}>{t("purchase_unit")}</Col>
                                    <Col span={3}>{t("purchase_qty")}</Col>
                                    <Col span={4}>{t("purchase_buy_price")}</Col>
                                    <Col span={4}>{t("purchase_length")}</Col>
                                <Col span={2}></Col>
                            </Row>
                                {fields.map(({ key, name, ...restField }, idx) => {
                                const unit = form.getFieldValue(['items', name, 'unit']);
                                const showUnitLength = unit === "roll" || unit === "set" || unit === "carton" || unit === "box"
                                return (
                                    <Row key={key} gutter={8} style={{ marginBottom: 6 }}>
                                        <Col span={6}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'product']}
                                                rules={[{ required: true, message: t("purchase_product") as string }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    options={productOptions}
                                                    placeholder={t("purchase_product")}
                                                    showSearch
                                                    optionFilterProp="label"
                                                    onChange={(value) => handleProductSelect(value, idx)}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            focusOnNewProductInput(idx + 1);
                                                            add();
                                                        }
                                                    }}
                                                    id={`item-${idx}`}
                                                />
                                            </Form.Item>
                                            <ItemStockHint form={form} name={name} products={products} type="purchase" />
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'unit']}
                                                rules={[{ required: true, message: t("purchase_unit") as string }]}
                                                style={{ marginBottom: 0 }}
                                                initialValue={UNITS[0].value}
                                            >
                                                <Select
                                                    options={UNITS}
                                                    placeholder={t("purchase_unit")}
                                                    onChange={() => form.validateFields()} // rerender for dynamic field
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'qty']}
                                                rules={[{ required: true, message: t("purchase_enter_qty") as string }]}
                                                style={{ marginBottom: 0 }}
                                                initialValue={1}
                                            >
                                                <InputNumber min={1} placeholder={t("purchase_qty") as string} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'buyPrice']}
                                                rules={[{ required: true, message: t("purchase_enter_buy_price") as string }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber min={0} placeholder={t("purchase_buy_price") as string} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        {/* Dynamic Length Field */}
                                        <Col span={4}>
                                            {showUnitLength && (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'unitLength']}
                                                    rules={[{ required: true, message: t("purchase_enter_length") as string }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber min={0} placeholder={t("purchase_length") as string} style={{ width: "100%" }} />
                                                </Form.Item>
                                            )}
                                        </Col>
                                        <Col span={2} style={{ display: "flex", alignItems: "center" }}>
                                            <Button
                                                type="link"
                                                danger
                                                icon={<MinusCircleOutlined />}
                                                onClick={() => remove(name)}
                                                tabIndex={-1}
                                            />
                                        </Col>
                                    </Row>
                                );
                            })}
                            <Form.ErrorList errors={errors} />
                            <Button
                                type="dashed"
                                block
                                icon={<PlusOutlined />}
                                    onClick={() => {
                                        add();
                                        focusOnNewProductInput(fields.length);
                                    }}
                                style={{ marginTop: 4 }}
                            >
                                    {t("purchase_add_item")}
                            </Button>
                        </>
                    )}
                </Form.List>
            </Card>
            <Row gutter={16}>
                <Col xs={24} md={6}>
                        <Form.Item name="total" label={t("purchase_total")} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: "100%" }} readOnly />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                        <Form.Item name="paid" label={t("purchase_paid")} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                        <Form.Item name="remain" label={t("purchase_remain")} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: "100%" }} readOnly />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                        <Form.Item name="note" label={t("purchase_note")}>
                            <Input.TextArea placeholder={t("purchase_note") as string} autoSize />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                    <Row gutter={12}>
                        <Col>
                            <Button type="primary" htmlType="submit" icon={<FileAddOutlined />} size="large">
                                {editPurchase ? t("purchase_update") : t("purchase_create")}
                            </Button>
                        </Col>
                        <Col>
                            <Button type="default" onClick={handlePrint} disabled={!newInvoice} icon={<PrinterOutlined />} size="large">
                                {t("purchase_print")}
                            </Button>
                        </Col>
                        <Col>
                            <Button type="dashed" onClick={handleNewInvoice} icon={<FileAddOutlined />} size="large">
                                {t("purchase_new_invoice")}
                            </Button>
                        </Col>
                    </Row>
            </Form.Item>
        </Form>
            {/* Hidden printable invoice section */}
            <div style={{ display: 'none' }}>
                {newInvoice && (
                    <PrintableInvoice
                        ref={printRef}
                        type="purchase"
                        invoice={newInvoice}
                        getCustomerOrSupplierName={() => getSupplierName(newInvoice.supplier)}
                        getProductName={getProductName}
                    />
                )}
            </div>
        </>
    );
};

export default PurchaseForm;
